import { useCallback, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { useLoading } from "../../../../../hooks/form";
import { usePolling } from "../../usePolling";
import actions from "../../../../../../actions";
import { publishPollResultIndicatesFailure } from "src/Manager/Campaigns/Ads/CampaignDetail/publishPollingUtils";
import {
  DisplayMarketingCampaign,
  CreateMarketingAdsetRequest,
  DisplayMarketingAdSet,
  CreateMarketingCampaignRequest,
  UpdateAdCreativeRequest,
  MarketingAd,
} from "common/interfaces/KYDMarketing";

interface UseManagedCampaignsProps {
  defaultCampaign: DisplayMarketingCampaign | null;
  defaultEditingAdset: DisplayMarketingAdSet | null;
  pages: { id: string; name: string }[];
  instagramAccounts: { id: string; username: string }[];
  /** When in event context: after polling we call refetchEvent so parent gets fresh section data. */
  eventId?: string;
  refetchEvent?: (params: { event_id: string }) => Promise<any>;
  /**
   * After polling, non-event callers run `fetchCampaigns()` first; this runs next with that result
   * so parents can sync local list without a second request (e.g. `setCampaigns(next)`). Optional
   * when you only need a side effect (e.g. refetch a single campaign elsewhere).
   */
  onPollingComplete?: (
    campaigns?: DisplayMarketingCampaign[]
  ) => void | Promise<void>;
}

export const useManagedCampaigns = ({
  defaultCampaign,
  defaultEditingAdset,
  pages,
  instagramAccounts,
  eventId,
  refetchEvent,
  onPollingComplete,
}: UseManagedCampaignsProps) => {
  const toast = useToast();
  const [loading, setLoading] = useLoading<{
    adsets: boolean;
    campaigns: boolean;
    audiences: boolean;
    pixels: boolean;
    preview: boolean;
    publish: Record<string, boolean>;
    adaccounts: boolean;
    search: boolean;
    instagramAccounts: boolean;
    pages: boolean;
  }>({
    adsets: false,
    campaigns: false,
    audiences: false,
    pixels: false,
    preview: false,
    publish: {},
    adaccounts: false,
    search: false,
    instagramAccounts: false,
    pages: false,
  });

  const [loadingMessage, setLoadingMessage] = useState<Record<
    string,
    string
  > | null>({});

  const checkCampaign = useCallback(
    async (campaign_id: string, update_id: string) => {
      const campaign = await actions.ADMIN.checkCampaignUpdate(
        campaign_id,
        update_id
      );
      return campaign;
    },
    []
  );

  const { start: startCampaignPolling, result: campaignResult } =
    usePolling<any>({
      maxRuns: 30,
      waitFor: 5000,
      checkResult: (result) => result.status !== "PROCESSING",
      poll: async (args: {
        campaign_id: string;
        update_id: string;
        onStatusUpdate?: (status: string | null) => void;
      }) => {
        const campaign = await checkCampaign(args.campaign_id, args.update_id);

        if (campaign.status !== "PROCESSING") {
          setLoadingMessage({});
          // Do not call onStatusUpdate(null) here — parent UIs should keep the last
          // progress line until pollForResult's onDone clears (avoids an empty gap).
        } else {
          setLoadingMessage((loadingMessage) => ({
            ...loadingMessage,
            [args.campaign_id]: campaign.update_status,
          }));
          const msg = campaign.update_status;
          if (msg != null && String(msg).trim() !== "") {
            args.onStatusUpdate?.(msg);
          }
        }

        return campaign;
      },
    });

  const fetchCampaigns = useCallback(async () => {
    setLoading("campaigns", true);
    try {
      const response = await actions.ADMIN.listCampaigns();
      return response.campaigns || [];
    } finally {
      setLoading("campaigns", false);
    }
  }, [setLoading]);

  const pollForResult = useCallback(
    async ({
      campaign_id,
      update_id,
      title,
      description,
      onStatusUpdate,
      onDone,
    }: {
      campaign_id: string;
      update_id: string;
      title?: string;
      description?: string;
      onStatusUpdate?: (status: string | null) => void;
      /** Called with true when publish/update succeeded, false on failure or timeout. */
      onDone?: (success: boolean) => void;
    }) => {
      setLoading("publish", {
        ...loading.publish,
        [campaign_id]: true,
      });
      let settledOk = false;
      try {
        const result = await startCampaignPolling({
          campaign_id,
          update_id,
          onStatusUpdate,
        });

        settledOk = !publishPollResultIndicatesFailure(result);

        if (settledOk) {
          toast({
            title: title || "Campaign published",
            description: description || "Campaign published",
            status: "success",
          });
        } else {
          const errMsg =
            result != null &&
            typeof result === "object" &&
            "error" in result &&
            typeof (result as { error?: string }).error === "string"
              ? (result as { error: string }).error
              : "Unable to complete campaign publish";
          toast({
            title: "Error publishing campaign",
            description: errMsg,
            status: "error",
          });
        }

        // Dev-style: when in event context, refetch event so section data (including campaigns) updates
        if (refetchEvent && eventId) {
          await refetchEvent({ event_id: eventId });
        } else {
          const nextCampaigns = await fetchCampaigns();
          await onPollingComplete?.(nextCampaigns);
        }
      } finally {
        setLoading("publish", {
          ...loading.publish,
          [campaign_id]: false,
        });
        onDone?.(settledOk);
      }
    },
    [
      startCampaignPolling,
      toast,
      setLoading,
      loading,
      fetchCampaigns,
      onPollingComplete,
      refetchEvent,
      eventId,
    ]
  );

  const createCampaign = useCallback(
    async (campaignData: CreateMarketingCampaignRequest) => {
      const publishKey = campaignData.id ?? "__creating__";
      setLoading("campaigns", true);
      setLoading("publish", {
        ...loading.publish,
        [publishKey]: true,
      });

      const method = campaignData.id
        ? actions.ADMIN.updateCampaign
        : actions.ADMIN.createCampaign;

      const event_ids = campaignData.event_ids?.filter(Boolean) || [];
      const payload = { ...campaignData, event_ids };
      return method(payload)
        .then(async (r: { update_id?: string; campaign?: { id?: string } }) => {
          const campaignId = r.campaign?.id || campaignData.id;
          if (r.update_id && campaignId) {
            setLoading("publish", {
              ...loading.publish,
              [publishKey]: false,
            });
            pollForResult({
              campaign_id: campaignId,
              update_id: r.update_id,
              title: "Campaign published",
              description: "Campaign published",
            });
          } else {
            setLoading("publish", {
              ...loading.publish,
              [publishKey]: false,
            });
          }

          await fetchCampaigns();

          return r;
        })
        .catch((r) => {
          toast({
            title: "Error publishing campaign",
            description: r.message,
            status: "error",
          });

          setLoading("publish", {
            ...loading.publish,
            [publishKey]: false,
          });

          return Promise.reject(r);
        })
        .finally(() => {
          setLoading("campaigns", false);
        });
    },
    [loading, pollForResult, toast, setLoading, fetchCampaigns]
  );

  const publishCampaign = useCallback(
    (campaign: { id: string }, extra?: { publish_all_adsets?: boolean }) => {
      setLoading("publish", {
        ...loading.publish,
        [campaign.id]: true,
      });
      return actions.ADMIN.publishCampaign({
        campaign_id: campaign.id,
        publish_all_adsets: extra?.publish_all_adsets,
      })
        .then(async (r) => {
          if (r.update_id) {
            pollForResult({
              campaign_id: campaign.id,
              update_id: r.update_id,
              title: "Campaign published",
              description: "Campaign published",
            });
          } else {
            setLoading("publish", {
              ...loading.publish,
              [campaign.id]: false,
            });
          }

          await fetchCampaigns();
          return r;
        })
        .catch((r) => {
          toast({
            title: "Error publishing campaign",
            description: r.message,
            status: "error",
          });

          setLoading("publish", {
            ...loading.publish,
            [campaign.id]: false,
          });

          return Promise.reject(r);
        });
    },
    [pollForResult, loading, toast, setLoading, fetchCampaigns]
  );

  const unpublishCampaign = useCallback(
    (campaign: { id: string; name: string }) => {
      setLoading("publish", {
        ...loading.publish,
        [campaign.id]: true,
      });

      return actions.ADMIN.unpublishCampaign({
        campaign_id: campaign.id,
      })
        .then(async (r) => {
          if (r.update_id) {
            pollForResult({
              campaign_id: campaign.id,
              update_id: r.update_id,
              title: `Campaign "${campaign.name}" unpublished from Meta`,
              description: `Campaign "${campaign.name}" unpublished from Meta`,
            });
          } else {
            setLoading("publish", {
              ...loading.publish,
              [campaign.id]: false,
            });
          }

          await fetchCampaigns();
          return r;
        })
        .catch((r) => {
          toast({
            title: `Error unpublishing campaign "${campaign.name}"`,
            description: r.message,
            status: "error",
          });

          setLoading("publish", {
            ...loading.publish,
            [campaign.id]: false,
          });

          return Promise.reject(r);
        });
    },
    [pollForResult, loading, toast, setLoading, fetchCampaigns]
  );

  const publishAdset = useCallback(
    ({ adset_id, campaign_id }: { adset_id: string; campaign_id: string }) => {
      setLoading("publish", {
        ...loading.publish,
        [campaign_id]: true,
      });

      return actions.ADMIN.publishAdset({
        adset_id,
        campaign_id,
      })
        .then(async (r) => {
          if (r.update_id) {
            pollForResult({
              campaign_id,
              update_id: r.update_id,
              title: "Adset published to Meta",
              description: "Adset published to Meta",
            });
          } else {
            setLoading("publish", {
              ...loading.publish,
              [campaign_id]: false,
            });
          }

          await fetchCampaigns();
          return r;
        })
        .catch((r) => {
          toast({
            title: "Error publishing adset",
            description: r.message,
            status: "error",
          });

          setLoading("publish", {
            ...loading.publish,
            [campaign_id]: false,
          });

          return Promise.reject(r);
        });
    },
    [pollForResult, loading, toast, setLoading, fetchCampaigns]
  );

  const unpublishAdset = useCallback(
    ({ adset_id, campaign_id }: { adset_id: string; campaign_id: string }) => {
      setLoading("publish", {
        ...loading.publish,
        [campaign_id]: true,
      });

      return actions.ADMIN.unpublishAdset({
        campaign_id,
        adset_id,
      })
        .then(async (r) => {
          if (r.update_id) {
            pollForResult({
              campaign_id,
              title: "Adset unpublished from Meta",
              description: "Adset unpublished from Meta",
              update_id: r.update_id,
            });
          } else {
            setLoading("publish", {
              ...loading.publish,
              [campaign_id]: false,
            });
          }

          await fetchCampaigns();
          return r;
        })
        .catch((r) => {
          toast({
            title: "Error unpublishing adset",
            description: r.message,
            status: "error",
          });

          setLoading("publish", {
            ...loading.publish,
            [campaign_id]: false,
          });

          return Promise.reject(r);
        });
    },
    [pollForResult, loading, toast, setLoading, fetchCampaigns]
  );

  const duplicateAdset = useCallback(
    (adset: DisplayMarketingAdSet) => {
      setLoading("adsets", true);

      return actions.ADMIN.duplicateAdset({
        campaign_id: adset.parent_id,
        adset_id: adset.id,
      })
        .then((r) => {
          return r;
        })
        .finally(() => {
          setLoading("adsets", false);
        });
    },
    [toast, setLoading]
  );

  const deleteAdset = useCallback(
    (adset: DisplayMarketingAdSet) => {
      setLoading("adsets", true);

      return actions.ADMIN.deleteAdset({
        campaign_id: adset.parent_id,
        adset_id: adset.id,
      })
        .then((r) => {
          return r;
        })
        .finally(() => {
          setLoading("adsets", false);
        });
    },
    [toast, setLoading]
  );

  const deleteCreative = useCallback(
    (adset: DisplayMarketingAdSet, ad: MarketingAd) => {
      setLoading("adsets", true);

      return actions.ADMIN.deleteCreative({
        campaign_id: adset.parent_id,
        adset_id: adset.id,
        creative_id: ad.creative.id,
      })
        .then((r) => {
          return r;
        })
        .finally(() => {
          setLoading("adsets", false);
        });
    },
    [toast, setLoading]
  );

  const createAd = useCallback(
    (adData: CreateMarketingAdsetRequest) => {
      setLoading("adsets", true);
      if (adData.external_id) {
        setLoading("publish", {
          ...loading.publish,
          [adData.parent_id]: true,
        });
      }

      const method = adData.id
        ? actions.ADMIN.updateAdset
        : actions.ADMIN.createAdset;

      return method(adData)
        .then(async (r) => {
          setLoading("adsets", false);

          if (r.update_id) {
            pollForResult({
              campaign_id: adData.parent_id,
              update_id: r.update_id,
              title: "Adset published to Meta",
              description: "Adset published to Meta",
            });
          } else {
            toast({
              title: adData.id ? "Adset updated" : "Adset created",
              description: `Adset ${adData.name} updated`,
              status: "success",
            });
            setLoading("publish", {
              ...loading.publish,
              [adData.parent_id]: false,
            });
          }

          await fetchCampaigns();
          return r;
        })
        .catch((r) => {
          toast({
            title: "Error updating adset",
            description: r.message,
            status: "error",
          });

          setLoading("adsets", false);
          setLoading("publish", {
            ...loading.publish,
            [adData.parent_id]: false,
          });

          return Promise.reject(r);
        });
    },
    [pollForResult, loading, toast, setLoading, fetchCampaigns]
  );

  const createAdCreative = useCallback(
    (adData: UpdateAdCreativeRequest) => {
      setLoading("adsets", true);
      if (adData.ads.some((ad) => ad.status === "ACTIVE")) {
        setLoading("publish", {
          ...loading.publish,
          [adData.campaign_id]: true,
        });
      }

      const method = actions.ADMIN.updateAdCreative;

      return method(adData)
        .then(async (r) => {
          setLoading("adsets", false);

          if (r.update_id) {
            pollForResult({
              campaign_id: adData.campaign_id,
              update_id: r.update_id,
              title: "Adcreative published to Meta",
              description: "Adcreative published to Meta",
            });
          } else {
            toast({
              title: "Creative updated",
              description: `Creative updated`,
              status: "success",
            });
            setLoading("publish", {
              ...loading.publish,
              [adData.campaign_id]: false,
            });
            setLoading("adsets", false);
          }

          await fetchCampaigns();
          return r;
        })
        .catch((r) => {
          console.log("r", r);
          toast({
            title: "Error updating adcreative",
            description: r.message,
            status: "error",
          });

          setLoading("adsets", false);
          setLoading("publish", {
            ...loading.publish,
            [adData.campaign_id]: false,
          });

          return Promise.reject(r);
        });
    },
    [
      defaultCampaign,
      defaultEditingAdset,
      pages,
      instagramAccounts,
      pollForResult,
      loading,
      toast,
      setLoading,
      fetchCampaigns,
    ]
  );

  const previewAd = useCallback(
    ({ adset_id, campaign_id }: { adset_id: string; campaign_id: string }) => {
      setLoading("preview", true);
      return actions.ADMIN.previewAd({
        adset_id,
        campaign_id,
      })
        .then((r) => {
          return r.previews.map((p) => ({
            name: p.name,
            html: p.preview.body,
          }));
        })
        .finally(() => {
          setLoading("preview", false);
        });
    },
    [setLoading]
  );

  const searchInterests = useCallback(
    (query: string) => {
      setLoading("search", true);
      return actions.ADMIN.searchInterests({
        query,
      }).finally(() => {
        setLoading("search", false);
      });
    },
    [setLoading]
  );

  const searchGeoMarkets = useCallback(
    (query: string) => {
      setLoading("search", true);
      return actions.ADMIN.searchGeoMarkets({
        query,
      }).finally(() => {
        setLoading("search", false);
      });
    },
    [setLoading]
  );

  const listPixels = useCallback(
    ({ ad_account_id }: { ad_account_id: string }) => {
      setLoading("pixels", true);
      return actions.ADMIN.listPixels({
        ad_account_id: ad_account_id,
      }).finally(() => {
        setLoading("pixels", false);
      });
    },
    [setLoading]
  );

  const listInstagramAccounts = useCallback(
    ({
      ad_account_id,
      facebook_page_id,
    }: {
      ad_account_id: string;
      facebook_page_id?: string;
    }) => {
      setLoading("instagramAccounts", true);
      return actions.ADMIN.listInstagramAccounts({
        ad_account_id,
        ...(facebook_page_id ? { facebook_page_id } : {}),
      }).finally(() => {
        setLoading("instagramAccounts", false);
      });
    },
    [setLoading]
  );

  const listPages = useCallback(
    ({ ad_account_id }: { ad_account_id: string }) => {
      setLoading("pages", true);
      return actions.ADMIN.listPages({
        ad_account_id,
      }).finally(() => {
        setLoading("pages", false);
      });
    },
    [setLoading]
  );

  return {
    // State
    loading,
    loadingMessage,

    // Campaign operations
    fetchCampaigns,
    createCampaign,
    publishCampaign,
    unpublishCampaign,

    // Adset operations
    createAd,
    publishAdset,
    unpublishAdset,
    deleteAdset,
    deleteCreative,
    duplicateAdset,

    // Ad creative operations
    createAdCreative,

    // Preview operations
    previewAd,

    // Search operations
    searchInterests,
    searchGeoMarkets,

    // List operations
    listPixels,
    listInstagramAccounts,
    listPages,

    // Polling
    pollForResult,
  };
};
